/* global Safe2Pay */
/* eslint-env browser */

import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static values = {
    orderTotal: Number,
    isOnlineCredit: { type: Boolean, default: false },
    // 3DS Config
    isEnabled: { type: Boolean, default: true },
    isSandbox: { type: Boolean, default: false },
    isDebug: { type: Boolean, default: false },
    isChallengeSuppressed: { type: Boolean, default: false },
    // Payment Config
    currency: { type: String, default: 'BRL' },
    installments: { type: String, default: '01' },
    orderRecurrence: { type: Boolean, default: false },
    orderProductcode: { type: String, default: 'PHY' },
    shipToShippingmethod: { type: String, default: 'lowcost' },
    // Error Messages
    errorFailure: { type: String, default: 'Não foi possível concluir a autenticação do cartão. Por favor, tente novamente ou utilize outro método de pagamento.' },
    errorUnenrolled: { type: String, default: 'O cartão utilizado não está habilitado para autenticação em compras online. Por favor, entre em contato com o emissor do cartão ou tente utilizar outro método de pagamento.' },
    errorDisabled: { type: String, default: 'Autenticação desabilitada pela loja.' },
    errorUnsupportedBrand: { type: String, default: 'A bandeira do cartão não suportada para autenticação. Por favor, entre em contato com o emissor do cartão ou tente utilizar outro método de pagamento.' },
    // Warning Messages
    warningSessionExpiredTitle: { type: String, default: 'Sessão expirada' },
    warningSessionExpiredDescription: { type: String, default: 'Os dados do cartão de crédito não foram encontrados. Por favor, preencha novamente.' },
    warningAuthFailureTitle: { type: String, default: 'Falha na autenticação' }
  }

  static targets = [
    'form',
    'submitButton',
    'cavv',
    'xid',
    'eci',
    'referenceId',
    'version',
    'warningTemplate'
  ]

  connect () {
    this.isAuthenticated = false
    this.isInitialized = false
    this.isInitializing = false
    this.needsReload = false
  }

  disableButton () {
    this.submitButtonTarget.classList.add('pointer-events-none', 'opacity-50')
  }

  authenticate (event) {
    if (!this.isOnlineCreditValue) return
    if (this.isAuthenticated) return

    event.preventDefault()
    this.submitButtonTarget.disabled = true

    this.cardData = this.#readCardData()

    if (!this.cardData) {
      this.#resetAfterFailure()
      this.#showWarning(this.warningSessionExpiredTitleValue, this.warningSessionExpiredDescriptionValue)
      return
    }

    if (!this.isInitialized) {
      this.#initialize3DS()
    } else {
      this.#handleCheckout()
    }
  }

  disconnect () {
    this.cardData = null
  }

  // Private

  #readCardData () {
    const raw = sessionStorage.getItem('_cs')
    if (!raw) return null

    const [cardNumber, expiration, holder, holderDocument] = atob(raw).split('|')
    const [month, year] = expiration.split('/')
    return { cardNumber, expirationMonth: month, expirationYear: year, holder, holderDocument }
  }

  #initialize3DS () {
    this.isInitializing = true

    const setup = () => {
      const eventHandlers = {
        onReady: this.#handleOnReady.bind(this),
        onSuccess: this.#handleOnSuccess.bind(this),
        onFailure: (e) => this.#handleEvent(e, this.errorFailureValue),
        onUnenrolled: (e) => this.#handleEvent(e, this.errorUnenrolledValue),
        onDisabled: (e) => this.#handleEvent(e, this.errorDisabledValue),
        onUnsupportedBrand: (e) => this.#handleEvent(e, this.errorUnsupportedBrandValue),
        onError: (e) => this.#handleOnError(e)
      }

      for (const [event, handler] of Object.entries(eventHandlers)) {
        Safe2Pay.Mpi.addEventListener(event, handler)
      }

      Safe2Pay.Mpi.Init(this.#config, this.orderTotalValue)
    }

    if (this.needsReload || typeof Safe2Pay === 'undefined') {
      this.#reloadSdkScript(setup)
    } else {
      setup()
    }
  }

  #handleCheckout () {
    Safe2Pay.Mpi.Checkout(this.#payment)
  }

  #handleOnReady () {
    this.isInitializing = false
    this.isInitialized = true
    this.#handleCheckout()
  }

  #handleOnSuccess (event) {
    this.cavvTarget.value = event.Cavv
    this.xidTarget.value = event.Xid
    this.eciTarget.value = event.Eci
    this.versionTarget.value = event.Version
    this.referenceIdTarget.value = event.ReferenceId

    sessionStorage.removeItem('_cs')
    this.isAuthenticated = true
    this.formTarget.requestSubmit()
  }

  #handleOnError (event) {
    console.error('[CREDIT_CARD_AUTH]', event)

    if (this.isInitializing) return

    this.#resetAfterFailure()
  }

  #handleEvent (_event, message) {
    this.#resetAfterFailure()
    this.#showWarning(this.warningAuthFailureTitleValue, message)
  }

  #resetAfterFailure () {
    this.isInitialized = false
    this.isInitializing = false
    this.needsReload = true
    this.submitButtonTarget.disabled = false
    this.submitButtonTarget.classList.remove('pointer-events-none', 'opacity-50')
  }

  #showWarning (title, description) {
    if (!this.hasWarningTemplateTarget) return

    document.getElementById('payment_online_warning_modal')?.remove()

    const html = this.warningTemplateTarget.innerHTML
      .replace('{{TITLE}}', title)
      .replace('{{DESCRIPTION}}', description)

    const container = document.getElementById('master_turbo') || document.body
    container.insertAdjacentHTML('beforeend', html)
  }

  #reloadSdkScript (callback) {
    const src = 'https://static.safe2pay.dev/scripts/verify_3DS2.min.js'
    const oldScript = document.querySelector(`script[src="${src}"]`)

    if (oldScript) oldScript.remove()
    try { delete window.Safe2Pay } catch (_) { window.Safe2Pay = undefined }

    const newScript = document.createElement('script')
    newScript.src = src
    newScript.onload = callback
    document.head.appendChild(newScript)
  }

  get #payment () {
    return {
      currency: this.currencyValue,
      installments: this.installmentsValue,
      order_recurrence: this.orderRecurrenceValue,
      order_productcode: this.orderProductcodeValue,
      shipTo_shippingmethod: this.shipToShippingmethodValue,
      billto_customerid: this.cardData.holderDocument,
      billto_contactname: this.cardData.holder,
      cardnumber: this.cardData.cardNumber,
      cardexpirationmonth: this.cardData.expirationMonth,
      cardexpirationyear: this.cardData.expirationYear,
      cardalias: this.cardData.holder
    }
  }

  get #config () {
    return {
      IsEnabled: this.isEnabledValue,
      IsSandbox: this.isSandboxValue,
      IsDebug: this.isDebugValue,
      IsChallengeSuppressed: this.isChallengeSuppressedValue
    }
  }
}
